import React, { useRef } from "react";
import { Typography, Box, Button, Grid, TextField, Chip } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Signature from "../../../../common/signature/signature";

const validationSchema = Yup.object().shape({
  childName: Yup.string().required("Child's name is required"),
  dob: Yup.date().required("Child's DOB is required"),
  dateOfAdmission: Yup.date().required("Date of admission is required"),
  socialWorker: Yup.string().required("Social Worker's name is required"),
  localAuthority: Yup.string().required("Local Authority is required"),
  registeredManagerName: Yup.string().required("Registered Manager Name is required"),
  responsibleIndividualName: Yup.string().required("Responsible Individual Name is required"),
});

const AdmissionConfirmation = () => {
  const signatureRef = useRef(null);
  const signatureRefPerson = useRef(null);
  const signatureRefManager = useRef(null);

  const initialValues = {
    childName: "",
    dob: "",
    dateOfAdmission: "",
    socialWorker: "",
    localAuthority: "",
    registeredManagerName: "",
    responsibleIndividualName: "",
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const signatureData = signatureRef.current?.getSignatureData();
        const signatureDataPerson = signatureRefPerson.current?.getSignatureData();
        const signatureDataManager = signatureRefManager.current?.getSignatureData();

        if (!signatureData || !signatureDataPerson || !signatureDataManager) {
          alert("Please provide a signature");
          return;
        }

        const formData = {
          ...values,
          signature: signatureData,
          signatureDataPerson: signatureDataPerson,
          signatureDataManager: signatureDataManager,
        };
        console.log("Form data submitted:", formData);
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur }) => (
        <Form className='admission-form'>
          <Box>
            <Chip label='Admission' />
            <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }} align='left' mt={1}>
              Admission Confirmation
            </Typography>
          </Box>

          <Grid container spacing={2} mt={2}>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Child's name
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your name'
                name='childName'
                value={values.childName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.childName && Boolean(errors.childName)}
                helperText={touched.childName && errors.childName}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Child's DOB
              </Typography>
              <TextField
                fullWidth
                name='dob'
                type='date'
                InputLabelProps={{ shrink: true }}
                value={values.dob}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.dob && Boolean(errors.dob)}
                helperText={touched.dob && errors.dob}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Date of admission
              </Typography>
              <TextField
                fullWidth
                name='dateOfAdmission'
                type='date'
                InputLabelProps={{ shrink: true }}
                value={values.dateOfAdmission}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.dateOfAdmission && Boolean(errors.dateOfAdmission)}
                helperText={touched.dateOfAdmission && errors.dateOfAdmission}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={2}>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Social Worker
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your name'
                name='socialWorker'
                value={values.socialWorker}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.socialWorker && Boolean(errors.socialWorker)}
                helperText={touched.socialWorker && errors.socialWorker}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Local Authority
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your name'
                name='localAuthority'
                value={values.localAuthority}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.localAuthority && Boolean(errors.localAuthority)}
                helperText={touched.localAuthority && errors.localAuthority}
              />
            </Grid>
          </Grid>

          <Typography sx={{ fontSize: "14px", color: "black" }} mt={4}>
            I confirm the above child has been considered suitable to reside with Active Care Organisation
            <Grid container>
              <Grid item>Ltd at </Grid>
              <Grid item>
                <Typography sx={{ fontSize: "14px", color: "#FFB700" }}> LittleNorway </Typography>
              </Grid>
              <Grid item>. I can also confirm prior to a decision being made a risk assessment, face to</Grid>
            </Grid>
            face meetings, and a transition period were completed.
          </Typography>

          {/* Signature */}
          <Box className='signature-box'>
            <Signature ref={signatureRef} />
            <Typography align='right'>
              <Grid container>
                <Grid>
                  <Typography sx={{ fontSize: "14px", color: "#FFB700" }}>Venorox </Typography>
                </Grid>
                <Grid>
                  <Grid>
                    <Typography sx={{ fontSize: "14px" }}>/ Signature</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid className='clear-signature-button'>
                <Button variant='outlined' onClick={() => signatureRef.current?.clearSignature()}>
                  Clear
                </Button>
              </Grid>
            </Typography>
          </Box>

          <Typography sx={{ fontSize: "14px", color: "black" }} mt={3}>
            I confirm we have commenced the placement for the above young person.
          </Typography>

          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Registered Manager Name
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your name'
                name='registeredManagerName'
                value={values.registeredManagerName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.registeredManagerName && Boolean(errors.registeredManagerName)}
                helperText={touched.registeredManagerName && errors.registeredManagerName}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                Responsible Individual Name
              </Typography>
              <TextField
                fullWidth
                placeholder='Enter your name'
                name='responsibleIndividualName'
                value={values.responsibleIndividualName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.responsibleIndividualName && Boolean(errors.responsibleIndividualName)}
                helperText={touched.responsibleIndividualName && errors.responsibleIndividualName}
              />
            </Grid>
          </Grid>

          {/* Signature */}
          <Grid container spacing={1} mt={3} ml={2}>
            <Grid item xs={12} sm={5}>
              <Box>
                <Signature ref={signatureRefPerson} />
                <Typography align='right'>
                  <Grid container>
                    <Grid>
                      <Typography sx={{ fontSize: "14px", color: "black" }}>Responsible person / Signature </Typography>
                      <Typography sx={{ fontSize: "14px" }}>{values.dateOfAdmission}</Typography>
                    </Grid>
                  </Grid>
                  <Grid container justifyContent='space-between' alignItems='center'>
                    <Button variant='outlined' onClick={() => signatureRefPerson.current?.clearSignature()}>
                      Clear
                    </Button>
                  </Grid>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={5}>
              <Box>
                <Signature ref={signatureRefManager} />
                <Typography align='right'>
                  <Grid container>
                    <Grid>
                      <Typography sx={{ fontSize: "14px", color: "black" }}>Registered Manager / Signature </Typography>
                      <Typography sx={{ fontSize: "14px" }}>{values.dateOfAdmission}</Typography>
                    </Grid>
                  </Grid>
                  <Grid container justifyContent='space-between' alignItems='center'>
                    <Button variant='outlined' onClick={() => signatureRefManager.current?.clearSignature()}>
                      Clear
                    </Button>
                  </Grid>
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Submit button */}
          <Grid item xs={12} mt={2}>
            <div className='submit-button-container'>
              <Button type='submit' variant='contained' color='primary' className='submit-button'>
                Submit
              </Button>
            </div>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default AdmissionConfirmation;
